import { observer } from "mobx-react-lite";
import { BreadCrumb } from "primereact/breadcrumb";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { Image } from "primereact/image";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SectionHeading from "../../../app/common/SectionHeading/SectionHeading";
import { RootStoreContext } from "../../../app/stores/rootStore";
import { appColors } from "../../../colors";

const BrandSettings = () => {
  const navigate = useNavigate();
  const toast = useRef(null);

  const [siteName, setSiteName] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const rootStore = useContext(RootStoreContext);
  const {
    appConfigurationsMap,
    fetchAppConfiguration,
    fetchingAppConfiguration,
    updateSiteName,
    updatingSiteName,
    uploadLogo,
    uploadingLogo,
    resetLogo,
  } = rootStore.appSettingsStore;

  const { appVars } = rootStore.generalStore;

  useEffect(() => {
    console.log("BrandSettings: mounting, fetching config only");
    fetchAppConfiguration();
  }, []); // only run once on mount

  useEffect(() => {
    // Load current site name from config
    const config = appConfigurationsMap.get("siteName");
    if (config) {
      setSiteName(config.value);
    }
  }, [appConfigurationsMap]);

  const breadCrumbItems = [
    {
      label: "App Settings",
      command: () => navigate("/admin/settings/"),
    },
    { label: "Brand Settings" },
  ];

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!selectedFile) {
      toast.current.show({
        severity: "warn",
        summary: "No file selected",
        detail: "Please select a logo image first",
      });
      return;
    }
    const res = await uploadLogo(selectedFile);
    if (res) {
      setSelectedFile(null);
      setLogoPreview(null);
      toast.current.show({
        severity: "success",
        summary: "Logo Updated",
        detail: "Logo has been uploaded successfully",
      });
    }
  };

  const handleResetLogo = async () => {
    const res = await resetLogo();
    if (res) {
      setSelectedFile(null);
      setLogoPreview(null);
      toast.current.show({
        severity: "info",
        summary: "Logo Reset",
        detail: "Logo has been reset to default",
      });
    }
  };

  const handleSaveSiteName = async () => {
    if (!siteName.trim()) {
      toast.current.show({
        severity: "warn",
        summary: "Name required",
        detail: "Site name cannot be empty",
      });
      return;
    }
    const res = await updateSiteName(siteName.trim());
  };

  const currentLogoUrl = appVars?.logoUrl || null;

  console.log("BrandSettings: rendering with appVars:", appVars, "configMap size:", appConfigurationsMap.size);
  return (
    <React.Fragment>
      <Toast ref={toast} />
      <div className="flex flex-column gap-3 w-full p-3">
        <div className="flex w-full">
          <BreadCrumb model={breadCrumbItems} />
        </div>

        <div className="flex w-full">
          <SectionHeading
            icon="ri-paint-brush-fill"
            heading="Brand Settings"
            displayHorizon={false}
            color={appColors.blocks.green}
          />
        </div>

        <div className="grid w-full">
          {/* Site Name Card */}
          <div className="col-6">
            <Card title="Site Name">
              <p className="text-secondary mb-3">
                Change the platform name that appears in the title bar, login
                page, and browser tab.
              </p>
              <div className="flex flex-column gap-2">
                <label htmlFor="siteName">Platform Name</label>
                <InputText
                  id="siteName"
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  placeholder="e.g., DAIKON"
                  className="w-full"
                />
                <Button
                  label="Save Name"
                  icon="pi pi-check"
                  className="p-button-warning mt-2"
                  onClick={handleSaveSiteName}
                  loading={updatingSiteName}
                />
              </div>
            </Card>
          </div>

          {/* Logo Card */}
          <div className="col-6">
            <Card title="Logo">
              <p className="text-secondary mb-3">
                Upload a custom logo image (PNG, JPEG, GIF, or SVG). Optimal
                size: 378×160 px.
              </p>

              {/* Current logo preview */}
              {(currentLogoUrl || logoPreview) && (
                <div className="mb-3 p-2 border-1 surface-border border-round">
                  <p className="text-sm mb-2">
                    <strong>Preview:</strong>
                  </p>
                  <img
                    src={logoPreview || currentLogoUrl}
                    alt="Logo preview"
                    style={{ maxWidth: "200px", maxHeight: "100px" }}
                  />
                </div>
              )}

              <div className="flex flex-column gap-2">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/svg+xml"
                  onChange={handleFileSelect}
                  className="mb-2"
                />

                <div className="flex gap-2">
                  <Button
                    label="Upload Logo"
                    icon="pi pi-upload"
                    className="p-button-warning"
                    onClick={handleUploadLogo}
                    loading={uploadingLogo}
                    disabled={!selectedFile}
                  />
                  <Button
                    label="Reset to Default"
                    icon="pi pi-undo"
                    className="p-button-outlined p-button-danger"
                    onClick={handleResetLogo}
                    loading={uploadingLogo}
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default observer(BrandSettings);